/** Anmeldeseite. Umleitungs-Flow, damit Popup-Blocker keine Rolle spielen. */
import { useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { Body1, Button, Card, Caption1, Spinner, Title2, makeStyles, tokens } from '@fluentui/react-components';
import { AuditLogo, AuditWordmark } from '../components/AuditLogo.js';
import { getAuthConfig } from '../lib/authConfig.js';
import { KRANNICH_YELLOW } from '../lib/brand.js';
import { useI18n } from '../i18n/index.js';

const useStyles = makeStyles({
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacingVerticalXL,
    backgroundColor: tokens.colorNeutralBackground2,
    // Zurueckhaltender Markenschimmer: ein weicher gelber Verlauf hinter der
    // Karte. Bewusst nur als Hintergrund und nicht hinter dem Text, damit die
    // Lesbarkeit unabhaengig vom Farbschema bleibt.
    backgroundImage: `radial-gradient(circle at 50% 0%, ${KRANNICH_YELLOW}22, transparent 60%)`,
  },
  card: {
    maxWidth: '440px',
    width: '100%',
    padding: tokens.spacingVerticalXXL,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalL,
    textAlign: 'center',
  },
  title: { letterSpacing: '0.01em' },
  // Gelber Balken unter dem Namen – dieselbe Kante wie in der Navigation.
  rule: {
    width: '48px',
    height: '4px',
    backgroundColor: KRANNICH_YELLOW,
    borderRadius: tokens.borderRadiusCircular,
  },
  vendor: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: tokens.spacingVerticalXS },
  vendorLogo: { height: '56px', width: 'auto' },
  hint: { color: tokens.colorNeutralForeground3 },
  error: { color: tokens.colorPaletteRedForeground1 },
});

export function SignInPage() {
  const styles = useStyles();
  const { instance } = useMsal();
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const signIn = async () => {
    setBusy(true);
    setError(null);
    try {
      await instance.loginRedirect(getAuthConfig().loginRequest);
    } catch (e: unknown) {
      setBusy(false);
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <main className={styles.page}>
      <Card className={styles.card}>
        <AuditLogo size={88} variant="full" title={t('app.title')} />
        <Title2 as="h1" className={styles.title}>
          <AuditWordmark />
        </Title2>
        <div className={styles.rule} aria-hidden />
        <Body1>{t('auth.signInPrompt')}</Body1>
        {busy ? (
          <Spinner label={t('auth.signingIn')} />
        ) : (
          <Button appearance="primary" size="large" onClick={() => void signIn()}>
            {t('auth.signIn')}
          </Button>
        )}
        {error ? <Body1 className={styles.error} role="alert">{error}</Body1> : null}
        <Caption1 className={styles.hint}>{t('auth.signInHint')}</Caption1>

        {/*
          Platz fuer das Krannich-KI-Logo. Die Datei wird unter
          web/public/krannich-ai.svg (oder .png) abgelegt; fehlt sie, wird das
          Bild ausgeblendet, statt ein kaputtes Symbol zu zeigen. So bleibt der
          Build unabhaengig davon, ob die Datei vorliegt.
        */}
        <div className={styles.vendor}>
          <img
            className={styles.vendorLogo}
            src="/krannich-ai.svg"
            alt=""
            aria-hidden
            onError={(event) => {
              event.currentTarget.style.display = 'none';
            }}
          />
          <Caption1 className={styles.hint}>{t('auth.vendorNote')}</Caption1>
        </div>
      </Card>
    </main>
  );
}
